import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PromptValues,
  PromptType,
  PromptStatus,
} from '@alucio/aws-beacon-amplify/src/models';
import {
  commonReducers,
  initialState,
  SliceState,
} from './common';
import { DataStore } from 'aws-amplify/datastore';
import ActiveUser from '../../global/ActiveUser';
import * as logger from 'src/utils/logger';

const sliceName = 'promptValues';

const { reducers, extraReducers } = commonReducers<PromptValues>(sliceName);

export interface CreatePromptValuePayload {
  promptType: PromptType;
  text: string;
  callback?: (value: PromptValues) => void;
}

export interface UpdatePromptValuePayload {
  value: PromptValues;
  updatedText: string;
  callback?: (value: PromptValues) => void;
}

const promptValuesSlice = createSlice({
  name: sliceName,
  initialState: initialState<PromptValues>(),
  reducers: {
    ...reducers,
    create: (
      _state: SliceState<PromptValues>,
      action: PayloadAction<CreatePromptValuePayload>,
    ): void => {
      const { payload } = action;
      const user = ActiveUser.user;
      const now = new Date().toISOString();

      if (!user) {
        logger.compliance.error('User not authenticated when trying to create prompt value');
        return;
      }

      const newValue = new PromptValues({
        tenantId: user.tenantId,
        promptType: payload.promptType,
        status: PromptStatus.ACTIVE,
        value: payload.text,
        createdAt: now,
        createdBy: user.id,
        updatedAt: now,
        updatedBy: user.id,
        editPermissions: [`TenantPublisher-${user.tenantId}`],
      });

      DataStore.save(newValue).then((value) => {
        payload.callback?.(value);
        analytics?.track('PROMPT_VALUE_CREATE', {
          action: 'CREATE',
          category: 'PROMPT_VALUES',
          promptType: payload.promptType,
          valueId: value.id,
        });
      }).catch((e) => {
        const errorText = 'Failed to create new prompt value';
        logger.compliance.error(errorText, e);
        throw Error(errorText, e);
      });
    },

    updatePromptValue: (
      _state: SliceState<PromptValues>,
      action: PayloadAction<UpdatePromptValuePayload>,
    ): void => {
      const { value, updatedText, callback } = action.payload;
      const user = ActiveUser.user;
      const now = new Date().toISOString();

      if (!user) {
        logger.compliance.error('User not authenticated when trying to update prompt value');
        return;
      }

      DataStore.query(PromptValues, value.id).then((original) => {
        if (!original) {
          throw new Error(`PromptValue with id ${value.id} not found`);
        }

        const updatedValue = PromptValues.copyOf(original, (updated) => {
          updated.value = updatedText;
          updated.updatedAt = now;
          updated.updatedBy = user.id;
        });

        return DataStore.save(updatedValue);
      }).then((savedValue) => {
        callback?.(savedValue);
        analytics?.track('PROMPT_VALUE_UPDATE', {
          action: 'UPDATE',
          category: 'PROMPT_VALUES',
          promptType: savedValue.promptType,
          valueId: savedValue.id,
        });
      }).catch((e) => {
        const errorText = 'Failed to update prompt value';
        logger.compliance.error(errorText, e);
        throw Error(errorText, e);
      });
    },
    deleteValue: {
      prepare: (value: PromptValues) => {
        analytics?.track('PROMPT_VALUE_DELETE', {
          action: 'DELETE',
          category: 'PROMPT_VALUES',
          promptType: value.promptType,
          valueId: value.id,
        });
        return {
          payload: {
            model: PromptValues,
            entity: value,
            updates: {
              updatedAt: (new Date().toISOString()),
              updatedBy: ActiveUser?.user?.id,
              status: PromptStatus.DELETED,
            },
          },
        };
      },
      reducer: (state: SliceState<PromptValues>, action: PayloadAction<{
        model: typeof PromptValues,
        entity: PromptValues,
        updates: Partial<PromptValues>
      }>) => {
        const { model, entity, updates } = action.payload;
        // Save to DataStore with soft delete status
        DataStore.query(model, entity.id).then((original) => {
          if (!original) {
            throw new Error(`PromptValue with id ${entity.id} not found`);
          }
          const updatedValue = model.copyOf(original, (updated) => {
            Object.assign(updated, updates);
          });
          return DataStore.save(updatedValue);
        }).catch((e) => {
          logger.compliance.error('Failed to soft delete prompt value', e);
        });

        // Immediately remove from Redux state for instant UI update
        const foundIdx = state.records.findIndex(record => record.id === entity.id);
        if (foundIdx !== -1) {
          state.records.splice(foundIdx, 1);
        }
        return state;
      },
    },
  },
  extraReducers,
});

export default promptValuesSlice;
export const promptValuesActions = {
  ...promptValuesSlice.actions,
};
